import { styled, alpha } from "@mui/material/styles";

export const StyledMarketplace = styled("div")(({ theme }) => ({
  padding: "24px 32px 48px",
  minHeight: "calc(100vh - 64px)",
  background:
    theme.palette.mode === "dark"
      ? "transparent"
      : "linear-gradient(180deg, #fdf7ff 0%, #f2f2ff 100%)",
}));

export const StyledHero = styled("div")({
  borderRadius: 24,
  padding: 32,
  marginBottom: 24,
  background: "linear-gradient(135deg, rgba(139, 92, 246, 0.6), rgba(99, 102, 241, 0.4))",
  color: "#fff",
  border: "1px solid rgba(255, 255, 255, 0.4)",
  boxShadow: "0 20px 60px rgba(99, 102, 241, 0.2)",
});

export const StyledHeroTitle = styled("h1")({
  fontSize: 36,
  fontWeight: 700,
  margin: "0 0 8px",
});

export const StyledHeroSubtitle = styled("p")({
  margin: 0,
  maxWidth: 600,
  fontSize: 16,
  lineHeight: 1.6,
});

export const StyledFilters = styled("div")({
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: 12,
  marginBottom: 24,
});

export const StyledSearchField = styled("div")({
  flex: "1 1 320px",
});

export const StyledCategoryPills = styled("div")({
  display: "flex",
  gap: 8,
  flexWrap: "wrap",
  width: "100%",
});

export const StyledProductGrid = styled("div")({
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
  gap: 20,
});

export const StyledProductCard = styled("div")(({ theme }) => ({
  background: theme.palette.background.paper,
  borderRadius: 20,
  border: `1px solid ${
    theme.palette.mode === "dark"
      ? alpha(theme.palette.common.white, 0.12)
      : alpha(theme.palette.common.black, 0.08)
  }`,
  padding: 24,
  display: "flex",
  flexDirection: "column",
  gap: 12,
  minHeight: 320,
  position: "relative",
  boxShadow:
    theme.palette.mode === "dark"
      ? "0 16px 40px rgba(0, 0, 0, 0.45)"
      : "0 8px 24px rgba(15, 23, 42, 0.06)",
}));

export const StyledProductHeader = styled("div")({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 8,
});

export const StyledBadge = styled("span", {
  shouldForwardProp: (prop) => prop !== "tint",
})(({ theme, tint }) => {
  const base = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "5px 12px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1.25,
    maxWidth: 148,
    textAlign: "center",
    boxSizing: "border-box",
  };

  if (theme.palette.mode === "dark") {
    if (tint) {
      return {
        ...base,
        color: theme.palette.secondary.light,
        background: `rgba(${tint}, 0.08)`,
        border: `1px solid rgba(${tint}, 0.42)`,
        boxShadow: `inset 0 1px 0 ${alpha(theme.palette.common.white, 0.06)}`,
      };
    }
    return {
      ...base,
      color: theme.palette.text.secondary,
      background: alpha(theme.palette.common.white, 0.05),
      border: `1px solid ${alpha(theme.palette.common.white, 0.14)}`,
    };
  }

  return {
    ...base,
    border: "1px solid transparent",
    color: theme.palette.text.primary,
    background: tint
      ? `rgba(${tint}, 0.15)`
      : alpha(theme.palette.common.black, 0.08),
  };
});

export const StyledTags = styled("div")({
  display: "flex",
  flexWrap: "wrap",
  gap: 6,
});

export const StyledTag = styled("span")(({ theme }) => ({
  background:
    theme.palette.mode === "dark"
      ? alpha(theme.palette.secondary.main, 0.22)
      : alpha(theme.palette.primary.main, 0.12),
  color: theme.palette.mode === "dark" ? theme.palette.secondary.light : "#4c1d95",
  borderRadius: 999,
  padding: "2px 10px",
  fontSize: 12,
}));

export const StyledProductDescription = styled("p")(({ theme }) => ({
  margin: 0,
  color: theme.palette.text.secondary,
  fontSize: 14,
  lineHeight: 1.5,
  flex: 1,
}));

export const StyledProductFooter = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 8,
});

export const StyledAiNotice = styled("div")(({ theme }) => ({
  fontSize: 13,
  color: theme.palette.text.primary,
  background:
    theme.palette.mode === "dark"
      ? alpha(theme.palette.success.main, 0.18)
      : alpha("#10b981", 0.12),
  borderRadius: 14,
  padding: "8px 12px",
  lineHeight: 1.4,
}));

export const StyledCategorySection = styled("div")(({ theme }) => ({
  background: theme.palette.background.paper,
  borderRadius: 24,
  padding: 24,
  border: `1px solid ${
    theme.palette.mode === "dark"
      ? alpha(theme.palette.common.white, 0.12)
      : alpha(theme.palette.common.black, 0.08)
  }`,
  boxShadow:
    theme.palette.mode === "dark"
      ? "0 15px 40px rgba(0, 0, 0, 0.45)"
      : "0 15px 40px rgba(15, 23, 42, 0.08)",
  marginBottom: 24,
}));

export const StyledCategoryHeader = styled("div")({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: 16,
  flexWrap: "wrap",
});

export const StyledCategoryList = styled("div")({
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: 16,
});

export const StyledCategoryCard = styled("div")(({ theme }) => ({
  background:
    theme.palette.mode === "dark"
      ? alpha(theme.palette.primary.main, 0.12)
      : "#f8f6ff",
  borderRadius: 18,
  padding: 16,
  border: `1px solid ${
    theme.palette.mode === "dark"
      ? alpha(theme.palette.secondary.main, 0.35)
      : alpha(theme.palette.primary.main, 0.2)
  }`,
  display: "flex",
  flexDirection: "column",
  gap: 6,
  minHeight: 180,
}));

export const StyledCategoryActions = styled("div")({
  display: "flex",
  gap: 8,
  flexWrap: "wrap",
});
